<template>
  <div id="app" v-i18n="locale">
    <Header />
    <main class="main-content">
      <router-view />
    </main>
    <SideDrawer />
  </div>
</template>

<script>
import Header from '@/components/Header.vue'
import SideDrawer from '@/components/SideDrawer.vue'
import { loginWithWallet } from '@/utils/auth'

export default {
  name: 'App',
  components: { Header, SideDrawer },
  computed: {
    locale() { return this.$store.state.locale }
  },
  data() {
    return {
      walletCheckInProgress: false,
      pendingAddress: '',
      walletProvider: null,
      walletPollTimer: null,
      userRefreshTimer: null,
      walletInitializedHandler: null
    }
  },
  mounted() {
    this.walletInitializedHandler = () => {
      this.bindWalletProvider()
      this.checkCurrentWallet()
    }
    window.addEventListener('ethereum#initialized', this.walletInitializedHandler, { once: false })
    this.bindWalletProvider()
    this.checkCurrentWallet()
    // Some wallet in-app browsers inject ethereum after Vue has mounted and
    // do not emit accountsChanged when the account was changed while away.
    this.walletPollTimer = window.setInterval(() => {
      this.bindWalletProvider()
      this.checkCurrentWallet()
    }, 1000)
    // Some wallet browsers keep the page alive without emitting provider events.
    // Refresh the server-side balance so admin adjustments appear without a reload.
    this.userRefreshTimer = window.setInterval(() => {
      if (!this.$store.getters.isLoggedIn || this.walletCheckInProgress) return
      const user = this.$store.state.user
      if (user && user.address) this.refreshUserInfo(this.normalizeAddress(user.address), this.$store.state.token)
    }, 15000)
    window.addEventListener('focus', this.checkCurrentWallet)
    window.addEventListener('pageshow', this.checkCurrentWallet)
    document.addEventListener('visibilitychange', this.checkCurrentWallet)
  },
  beforeDestroy() {
    if (this.walletPollTimer) window.clearInterval(this.walletPollTimer)
    if (this.userRefreshTimer) window.clearInterval(this.userRefreshTimer)
    if (this.walletProvider && this.walletProvider.removeListener) {
      this.walletProvider.removeListener('accountsChanged', this.handleAccountsChanged)
    }
    if (this.walletInitializedHandler) window.removeEventListener('ethereum#initialized', this.walletInitializedHandler)
    window.removeEventListener('focus', this.checkCurrentWallet)
    window.removeEventListener('pageshow', this.checkCurrentWallet)
    document.removeEventListener('visibilitychange', this.checkCurrentWallet)
  },
  methods: {
    bindWalletProvider() {
      const provider = window.ethereum
      if (!provider || provider === this.walletProvider) return
      if (this.walletProvider && this.walletProvider.removeListener) {
        this.walletProvider.removeListener('accountsChanged', this.handleAccountsChanged)
      }
      this.walletProvider = provider
      if (provider.on) provider.on('accountsChanged', this.handleAccountsChanged)
    },
    normalizeAddress(address) {
      return String(address || '').toLowerCase()
    },
    async checkCurrentWallet() {
      const provider = this.walletProvider || window.ethereum
      if (this.walletCheckInProgress || !provider || !provider.request) return
      try {
        const accounts = await provider.request({ method: 'eth_accounts' })
        const nextAddress = this.normalizeAddress(accounts && accounts[0])
        const loggedAddress = this.normalizeAddress(this.$store.state.user && this.$store.state.user.address)

        if (!this.$store.getters.isLoggedIn || !nextAddress) return
        if (nextAddress !== loggedAddress) {
          await this.handleAccountsChanged(accounts)
          return
        }

        // Only refresh the account that is still active when the response returns.
        await this.refreshUserInfo(nextAddress, this.$store.state.token)
      } catch (e) {
        // Passive account checks must not open a wallet permission prompt.
      }
    },
    async handleAccountsChanged(accounts) {
      const nextAddress = this.normalizeAddress(accounts && accounts[0])
      const currentAddress = this.normalizeAddress(this.$store.state.user && this.$store.state.user.address)
      if (!nextAddress || !this.$store.getters.isLoggedIn || nextAddress === currentAddress) return

      if (this.walletCheckInProgress) {
        this.pendingAddress = nextAddress
        return
      }

      this.walletCheckInProgress = true
      this.pendingAddress = ''
      this.$store.commit('CLEAR_ACTIVE_SESSION')

      try {
        const restored = await this.$store.dispatch('restoreWalletSession', nextAddress)
        if (restored && await this.refreshUserInfo(nextAddress, restored.token)) {
          this.$toast('已切换到已登录钱包', 'success')
          return
        }
        if (restored) await this.$store.dispatch('forgetWalletSession', nextAddress)

        this.$toast('新钱包需要签名授权登录', 'info')
        await loginWithWallet(this.$store, nextAddress)
        this.$toast('登录成功', 'success')
      } catch (err) {
        if (err && err.code === 4001) this.$toast('你取消了签名登录', 'error')
        if (err && err.code === 'WALLET_CHANGED') this.$toast('钱包又发生变化，请重新选择钱包', 'info')
      } finally {
        this.walletCheckInProgress = false
        if (this.pendingAddress && this.pendingAddress !== this.normalizeAddress(this.$store.state.user && this.$store.state.user.address)) {
          const pending = this.pendingAddress
          this.pendingAddress = ''
          this.$nextTick(() => this.handleAccountsChanged([pending]))
        }
      }
    },
    async refreshUserInfo(expectedAddress, expectedToken) {
      try {
        const { getUserProfile } = await import('@/api')
        const res = await getUserProfile()
        const activeAddress = this.normalizeAddress(this.$store.state.user && this.$store.state.user.address)
        const activeToken = this.$store.state.token
        if (res.code !== 0 || !res.data) return false
        if (expectedAddress && activeAddress !== expectedAddress) return false
        if (expectedToken && activeToken !== expectedToken) return false

        let accounts = []
        if (window.ethereum && window.ethereum.request) {
          accounts = await window.ethereum.request({ method: 'eth_accounts' })
        }
        if (accounts[0] && this.normalizeAddress(accounts[0]) !== expectedAddress) return false
        if (this.normalizeAddress(res.data.address) !== expectedAddress) return false

        this.$store.commit('SET_USER', res.data)
        return true
      } catch (e) {
        return false
      }
    }
  }
}
</script>

<style>
.main-content {
  padding-top: 56px;
  min-height: calc(100vh - 56px);
}
</style>
